/**
 * overlayTemplates — canvas renderers for the generated-graphic overlay blocks.
 *
 * Each template draws a transparent PNG (phone notification, incoming call
 * screen, location card) that the overlay block composites onto the clip. The
 * same renderer feeds the live editor preview and the PNG that gets uploaded,
 * so what you place is exactly what renders.
 *
 * A template declares:
 *   id, label, icon   - menu identity
 *   aspect            - width/height of the graphic it draws
 *   defaults          - { scale, x, y, animate, duration } placement on the clip
 *   fields            - [{ key, label, type, placeholder, max }] editor form
 *   summary(values)   - one-line label for the timeline step
 *   draw(ctx, w, h, values) - paints into a transparent canvas of w x h
 *
 * Everything is drawn in units relative to `w`, so a template is resolution
 * independent: the editor renders it at the exact pixel width it will occupy in
 * the finished video, keeping text crisp.
 */

const INK = '#0b0d12';
const MUTED = 'rgba(60,64,74,0.72)';

/** Rounded rectangle path (Path2D.roundRect isn't universal in older Safari). */
function roundRect(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
}

function shadowed(ctx, blur, offsetY, draw) {
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.30)';
    ctx.shadowBlur = blur;
    ctx.shadowOffsetY = offsetY;
    draw();
    ctx.restore();
}

const FONT = '-apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function font(size, weight = 400) {
    return `${weight} ${size}px ${FONT}`;
}

/** Draw text, truncating with an ellipsis once it exceeds maxWidth. */
function clippedText(ctx, text, x, y, maxWidth) {
    let value = String(text ?? '');
    if (ctx.measureText(value).width <= maxWidth) {
        ctx.fillText(value, x, y);
        return;
    }
    while (value.length > 1 && ctx.measureText(`${value}…`).width > maxWidth) {
        value = value.slice(0, -1);
    }
    ctx.fillText(`${value}…`, x, y);
}

/** Wrap text to `maxLines`, ellipsising the last line. Returns lines drawn. */
function wrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
    const words = String(text ?? '').split(/\s+/).filter(Boolean);
    const lines = [];
    let line = '';
    for (const word of words) {
        const next = line ? `${line} ${word}` : word;
        if (ctx.measureText(next).width > maxWidth && line) {
            lines.push(line);
            line = word;
            if (lines.length === maxLines) break;
        } else {
            line = next;
        }
    }
    if (lines.length < maxLines && line) lines.push(line);
    lines.slice(0, maxLines).forEach((text_, i) => {
        const isLast = i === Math.min(lines.length, maxLines) - 1;
        const overflowed = isLast && lines.length >= maxLines && ctx.measureText(text_).width > maxWidth;
        if (overflowed) clippedText(ctx, text_, x, y + i * lineHeight, maxWidth);
        else ctx.fillText(text_, x, y + i * lineHeight);
    });
    return Math.min(lines.length, maxLines);
}

/** A soft circular avatar with the contact's initials — no external images. */
function initialsAvatar(ctx, cx, cy, radius, name, hue) {
    const gradient = ctx.createLinearGradient(cx - radius, cy - radius, cx + radius, cy + radius);
    gradient.addColorStop(0, `hsl(${hue}, 62%, 58%)`);
    gradient.addColorStop(1, `hsl(${(hue + 40) % 360}, 58%, 44%)`);
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.fill();

    const initials = String(name || '?')
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((part) => part[0].toUpperCase()).join('') || '?';
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.font = font(radius * 0.9, 600);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initials, cx, cy + radius * 0.04);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
}

/** Map pin glyph, centred on (cx, cy) with the point at the bottom. */
function mapPin(ctx, cx, cy, size, color) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy - size * 0.35, size * 0.38, Math.PI, 0);
    ctx.bezierCurveTo(cx + size * 0.38, cy + size * 0.1, cx + size * 0.1, cy + size * 0.2, cx, cy + size * 0.5);
    ctx.bezierCurveTo(cx - size * 0.1, cy + size * 0.2, cx - size * 0.38, cy + size * 0.1, cx - size * 0.38, cy - size * 0.35);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.beginPath();
    ctx.arc(cx, cy - size * 0.33, size * 0.14, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
}

// --- notification ----------------------------------------------------------

const notification = {
    id: 'notification',
    label: 'Phone notification',
    icon: '🔔',
    aspect: 1000 / 260,
    defaults: { scale: 0.86, x: 0.5, y: 0.14, animate: 'top', duration: 2.5 },
    fields: [
        { key: 'app', label: 'App', type: 'text', placeholder: 'Messages', max: 24 },
        { key: 'title', label: 'From', type: 'text', placeholder: 'Mom', max: 40 },
        { key: 'body', label: 'Message', type: 'textarea', placeholder: 'Are you coming for dinner?', max: 140 },
        { key: 'time', label: 'Timestamp', type: 'text', placeholder: 'now', max: 16 },
        { key: 'theme', label: 'Theme', type: 'select', options: [
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
        ] },
    ],
    summary: (v) => `Notification: ${v.title || v.app || 'message'}`,
    draw(ctx, w, h, v) {
        const dark = v.theme === 'dark';
        const pad = w * 0.045;
        const radius = h * 0.30;

        shadowed(ctx, w * 0.03, h * 0.05, () => {
            ctx.fillStyle = dark ? 'rgba(28,30,36,0.94)' : 'rgba(250,250,252,0.94)';
            roundRect(ctx, 0, 0, w, h, radius);
            ctx.fill();
        });
        // Hairline edge keeps the card readable over a bright frame.
        ctx.strokeStyle = dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)';
        ctx.lineWidth = Math.max(1, w * 0.0015);
        roundRect(ctx, 0, 0, w, h, radius);
        ctx.stroke();

        const iconSize = h * 0.42;
        const iconX = pad;
        const iconY = (h - iconSize) / 2;
        const gradient = ctx.createLinearGradient(iconX, iconY, iconX + iconSize, iconY + iconSize);
        gradient.addColorStop(0, '#4f8bff');
        gradient.addColorStop(1, '#3358d4');
        ctx.fillStyle = gradient;
        roundRect(ctx, iconX, iconY, iconSize, iconSize, iconSize * 0.28);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.font = font(iconSize * 0.52);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('💬', iconX + iconSize / 2, iconY + iconSize / 2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';

        const textX = iconX + iconSize + pad * 0.8;
        const timeText = v.time || 'now';
        ctx.font = font(h * 0.135, 500);
        const timeWidth = ctx.measureText(timeText).width;
        const textW = w - textX - pad - timeWidth - pad * 0.5;

        ctx.fillStyle = dark ? 'rgba(255,255,255,0.55)' : MUTED;
        ctx.fillText(timeText, w - pad - timeWidth, h * 0.30);

        ctx.font = font(h * 0.145, 600);
        ctx.fillStyle = dark ? 'rgba(255,255,255,0.62)' : MUTED;
        clippedText(ctx, (v.app || 'Messages').toUpperCase(), textX, h * 0.30, textW);

        ctx.font = font(h * 0.20, 700);
        ctx.fillStyle = dark ? '#f5f6f8' : INK;
        clippedText(ctx, v.title || 'Mom', textX, h * 0.545, textW + timeWidth);

        ctx.font = font(h * 0.175, 400);
        ctx.fillStyle = dark ? 'rgba(255,255,255,0.78)' : 'rgba(40,44,52,0.86)';
        wrappedText(ctx, v.body || '', textX, h * 0.78, w - textX - pad, h * 0.205, 2);
    },
};

// --- incoming call ---------------------------------------------------------

const call = {
    id: 'call',
    label: 'Call screen',
    icon: '📞',
    aspect: 1000 / 1400,
    defaults: { scale: 0.55, x: 0.5, y: 0.5, animate: 'none', duration: 3 },
    fields: [
        { key: 'name', label: 'Caller', type: 'text', placeholder: 'Unknown Number', max: 32 },
        { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: 'mobile', max: 32 },
        { key: 'state', label: 'State', type: 'select', options: [
            { value: 'incoming', label: 'Incoming (accept / decline)' },
            { value: 'active', label: 'On call (timer)' },
        ] },
        { key: 'timer', label: 'Call timer', type: 'text', placeholder: '00:42', max: 10 },
    ],
    summary: (v) => `${v.state === 'active' ? 'Call' : 'Incoming call'}: ${v.name || 'Unknown'}`,
    draw(ctx, w, h, v) {
        const active = v.state === 'active';
        const radius = w * 0.09;

        shadowed(ctx, w * 0.06, h * 0.01, () => {
            const bg = ctx.createLinearGradient(0, 0, 0, h);
            bg.addColorStop(0, 'rgba(24,26,32,0.93)');
            bg.addColorStop(1, 'rgba(12,13,17,0.95)');
            ctx.fillStyle = bg;
            roundRect(ctx, 0, 0, w, h, radius);
            ctx.fill();
        });
        ctx.strokeStyle = 'rgba(255,255,255,0.12)';
        ctx.lineWidth = Math.max(1, w * 0.002);
        roundRect(ctx, 0, 0, w, h, radius);
        ctx.stroke();

        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.font = font(w * 0.048, 500);
        ctx.textAlign = 'center';
        ctx.fillText(active ? (v.timer || '00:42') : 'incoming call', w / 2, h * 0.135);

        initialsAvatar(ctx, w / 2, h * 0.33, w * 0.16, v.name, 214);

        // textAlign stays 'center', so these anchor at the card's midline.
        ctx.fillStyle = '#f6f7f9';
        ctx.font = font(w * 0.088, 600);
        clippedText(ctx, v.name || 'Unknown Number', w / 2, h * 0.545, w * 0.84);

        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.font = font(w * 0.05, 400);
        clippedText(ctx, v.subtitle || 'mobile', w / 2, h * 0.605, w * 0.8);
        ctx.textAlign = 'left';

        // Action buttons: decline + accept when ringing, a single hang-up when live.
        const btnR = w * 0.105;
        const btnY = h * 0.82;
        const drawBtn = (cx, color, glyph, rotate) => {
            shadowed(ctx, w * 0.04, h * 0.006, () => {
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(cx, btnY, btnR, 0, Math.PI * 2);
                ctx.fill();
            });
            ctx.save();
            ctx.translate(cx, btnY);
            if (rotate) ctx.rotate((135 * Math.PI) / 180);
            ctx.fillStyle = '#fff';
            ctx.font = font(btnR * 1.02);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(glyph, 0, btnR * 0.04);
            ctx.restore();
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
        };

        if (active) {
            drawBtn(w / 2, '#e5484d', '📞', true);
        } else {
            drawBtn(w * 0.29, '#e5484d', '📞', true);
            drawBtn(w * 0.71, '#30a46c', '📞', false);
        }
    },
};

// --- location card ---------------------------------------------------------

const LOCATION_STYLES = {
    day: { land: '#eef1f5', road: '#ffffff', roadEdge: '#dfe4ea', park: '#d9ead4', water: '#bcd8ef', ink: INK, sub: MUTED, card: 'rgba(252,252,253,0.96)' },
    night: { land: '#232833', road: '#39404e', roadEdge: '#2b313c', park: '#2a3a30', water: '#1f3446', ink: '#f2f4f7', sub: 'rgba(255,255,255,0.6)', card: 'rgba(26,29,36,0.95)' },
};

/**
 * A stylised street grid — deterministic from `seed`, so the same card always
 * redraws identically. Not a real map: it reads as one at video scale without
 * pulling in a tile API.
 */
function streetGrid(ctx, w, h, style, seed) {
    let state = (seed || 1) >>> 0;
    const rand = () => {
        // xorshift32: small, stable, no dependency.
        state ^= state << 13; state >>>= 0;
        state ^= state >> 17;
        state ^= state << 5; state >>>= 0;
        return state / 0xffffffff;
    };

    ctx.fillStyle = style.land;
    ctx.fillRect(0, 0, w, h);

    // A river and a park block, placed from the seed.
    ctx.fillStyle = style.water;
    ctx.beginPath();
    const riverY = h * (0.2 + rand() * 0.5);
    ctx.moveTo(-w * 0.1, riverY);
    ctx.bezierCurveTo(w * 0.3, riverY - h * 0.18, w * 0.6, riverY + h * 0.2, w * 1.1, riverY - h * 0.05);
    ctx.lineTo(w * 1.1, riverY + h * 0.12);
    ctx.bezierCurveTo(w * 0.6, riverY + h * 0.36, w * 0.3, riverY - h * 0.02, -w * 0.1, riverY + h * 0.14);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = style.park;
    const parkW = w * (0.16 + rand() * 0.14);
    const parkH = h * (0.14 + rand() * 0.16);
    roundRect(ctx, w * rand() * 0.6, h * rand() * 0.55, parkW, parkH, Math.min(parkW, parkH) * 0.12);
    ctx.fill();

    // Roads: a few majors plus a light minor grid, all axis-aligned with one
    // diagonal avenue so it doesn't read as graph paper.
    const drawRoad = (x1, y1, x2, y2, width) => {
        ctx.strokeStyle = style.roadEdge;
        ctx.lineWidth = width * 1.5;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
        ctx.strokeStyle = style.road;
        ctx.lineWidth = width;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    };

    ctx.lineCap = 'round';
    for (let i = 0; i < 5; i += 1) {
        const y = h * (0.1 + i * 0.2 + (rand() - 0.5) * 0.06);
        drawRoad(-w * 0.05, y, w * 1.05, y, w * (i % 2 === 0 ? 0.016 : 0.009));
    }
    for (let i = 0; i < 5; i += 1) {
        const x = w * (0.12 + i * 0.19 + (rand() - 0.5) * 0.05);
        drawRoad(x, -h * 0.05, x, h * 1.05, w * (i % 2 === 0 ? 0.014 : 0.008));
    }
    drawRoad(-w * 0.05, h * 0.95, w * 1.05, -h * 0.05, w * 0.02);
    ctx.lineCap = 'butt';
}

const location = {
    id: 'location',
    label: 'Location card',
    icon: '📍',
    aspect: 1000 / 760,
    defaults: { scale: 0.5, x: 0.72, y: 0.24, animate: 'right', duration: 3 },
    fields: [
        { key: 'place', label: 'Place', type: 'text', placeholder: 'Brooklyn Bridge', max: 40 },
        { key: 'detail', label: 'Detail', type: 'text', placeholder: 'New York, NY', max: 40 },
        { key: 'meta', label: 'Meta (ETA, distance…)', type: 'text', placeholder: '12 min · 3.4 mi', max: 32 },
        { key: 'theme', label: 'Map style', type: 'select', options: [
            { value: 'day', label: 'Day' },
            { value: 'night', label: 'Night' },
        ] },
        { key: 'route', label: 'Show route line', type: 'checkbox' },
    ],
    summary: (v) => `Location: ${v.place || 'map'}`,
    draw(ctx, w, h, v) {
        const style = LOCATION_STYLES[v.theme === 'night' ? 'night' : 'day'];
        const radius = w * 0.045;
        const mapH = h * 0.70;

        shadowed(ctx, w * 0.035, h * 0.012, () => {
            ctx.fillStyle = style.card;
            roundRect(ctx, 0, 0, w, h, radius);
            ctx.fill();
        });

        // Clip the generated streets to the card's rounded top.
        ctx.save();
        roundRect(ctx, 0, 0, w, h, radius);
        ctx.clip();
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, w, mapH);
        ctx.clip();
        // Seed from the place name so a given location always looks the same.
        const seed = String(v.place || 'map').split('').reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) >>> 0, 7) || 7;
        streetGrid(ctx, w, mapH, style, seed);

        if (v.route) {
            ctx.strokeStyle = '#3b82f6';
            ctx.lineWidth = w * 0.018;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(w * 0.16, mapH * 0.86);
            ctx.lineTo(w * 0.16, mapH * 0.56);
            ctx.lineTo(w * 0.46, mapH * 0.56);
            ctx.lineTo(w * 0.46, mapH * 0.34);
            ctx.lineTo(w * 0.62, mapH * 0.34);
            ctx.stroke();
            ctx.fillStyle = '#3b82f6';
            ctx.beginPath();
            ctx.arc(w * 0.16, mapH * 0.86, w * 0.022, 0, Math.PI * 2);
            ctx.fill();
        }

        mapPin(ctx, v.route ? w * 0.62 : w * 0.5, mapH * (v.route ? 0.34 : 0.52), w * 0.16, '#e5484d');
        ctx.restore();

        ctx.fillStyle = style.ink;
        ctx.font = font(w * 0.072, 700);
        clippedText(ctx, v.place || 'Brooklyn Bridge', w * 0.055, mapH + h * 0.115, w * 0.89);

        ctx.fillStyle = style.sub;
        ctx.font = font(w * 0.05, 400);
        const detail = [v.detail, v.meta].filter(Boolean).join('  ·  ');
        clippedText(ctx, detail || 'New York, NY', w * 0.055, mapH + h * 0.205, w * 0.89);
        ctx.restore();
    },
};

export const OVERLAY_TEMPLATES = [notification, call, location];

export function getOverlayTemplate(id) {
    return OVERLAY_TEMPLATES.find((t) => t.id === id) || OVERLAY_TEMPLATES[0];
}

/** Field defaults for a template — the placeholder text, so it never renders empty. */
export function templateDefaults(template) {
    const values = {};
    (template.fields || []).forEach((field) => {
        if (field.type === 'select') values[field.key] = field.options[0].value;
        else if (field.type === 'checkbox') values[field.key] = false;
        else values[field.key] = field.placeholder || '';
    });
    return values;
}

/**
 * Render a template to a canvas at `width` px (height follows its aspect).
 * Returns the canvas so callers can take a data URL for preview or a blob for
 * upload — both come from the same draw, so they can never disagree.
 */
export function renderTemplateCanvas(template, values, width) {
    const w = Math.max(2, Math.round(width));
    const h = Math.max(2, Math.round(w / template.aspect));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.textBaseline = 'alphabetic';
    template.draw(ctx, w, h, values || {});
    return canvas;
}

export function canvasToPngBlob(canvas) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the overlay image'))), 'image/png');
    });
}
